import { ThemeType } from '@app/interfaces/interfaces';
import { hexToRGB } from '@app/utils/utils';
import { css } from 'styled-components';
import { BASE_COLORS } from './constants';
import { darkColorsTheme, antDarkColorsTheme } from './dark/darkTheme';
import { lightColorsTheme } from './light/lightTheme';

export const themeObject = {
  light: lightColorsTheme,
  dark: darkColorsTheme
};

export const antThemeObject = {
  light: {},
  dark: antDarkColorsTheme
};

export const getThemeVariables = (
  theme: ThemeType,
  palette = themeObject[theme]
) => css`
  color-scheme: ${theme};
  --logo-text-color: ${palette.logoText};
  --logo-text-span-color: ${palette.logoTextSpan};
  --primary-color: ${palette.primary};
  --primary1-color: ${palette.primary1};
  --primary-gradient-color: ${palette.primaryGradient};
  --secondary-gradient-color: ${palette.secondaryGradient};
  --info-color: ${palette.primary};
  --secondary-color: ${palette.secondary};
  --error-color: ${palette.error};
  --warning-color: ${palette.warning};
  --success-color: ${palette.success};
  --background-color: ${palette.background};
  --secondary-background-color: ${palette.secondaryBackground};
  --secondary-background-selected-color: ${palette.secondaryBackgroundSelected};
  --additional-background-color: ${palette.additionalBackground};
  --collapse-background-color: ${palette.collapseBackground};
  --timeline-background-color: ${palette.timelineBackground};
  --spinner-base-color: ${palette.spinnerBase};
  --sider-background-color: ${palette.siderBackground};
  --shadow-color: ${palette.shadow};
  --border-color: ${palette.border};
  --border-nft-color: ${palette.borderNft};
  --scroll-color: ${palette.scroll};

  --primary-rgb-color: ${hexToRGB(palette.primary)};
  --info-rgb-color: ${hexToRGB(palette.primary)};
  --secondary-rgb-color: ${hexToRGB(palette.secondary)};
  --error-rgb-color: ${hexToRGB(palette.error)};
  --warning-rgb-color: ${hexToRGB(palette.warning)};
  --success-rgb-color: ${hexToRGB(palette.success)};
  --background-rgb-color: ${hexToRGB(palette.background)};

  --text-main-color: ${palette.textMain};
  --text-light-color: ${palette.textLight};
  --text-superLight-color: ${palette.textSuperLight};
  --text-secondary-color: ${palette.textSecondary};
  --text-dark-color: ${palette.textDark};
  --text-nft-light-color: ${palette.textNftLight};
  --text-sider-primary-color: ${palette.textSiderPrimary};
  --text-sider-secondary-color: ${palette.textSiderSecondary};
  --subtext-color: ${palette.subText};

  --dashboard-map-background-color: ${palette.dashboardMapBackground};
  --dashboard-map-circle-color: ${palette.dashboardMapCircleColor};
  --dashboard-map-control-disabled-background-color: ${palette.dashboardMapControlDisabledBackground};

  --chart-tooltip-label-color: ${palette.chartTooltipLabel};
  --chart-color1: ${palette.chartColor1};
  --chart-rgb-color1: ${hexToRGB(palette.chartColor1)};
  --chart-color1-tint: ${palette.chartColor1Tint};
  --chart-color2: ${palette.chartColor2};
  --chart-color2-tint: ${palette.chartColor2Tint};
  --chart-color3: ${palette.chartColor3};
  --chart-color3-tint: ${palette.chartColor3Tint};
  --chart-color4: ${palette.chartColor4};
  --chart-color4-tint: ${palette.chartColor4Tint};
  --chart-color5: ${palette.chartColor5};
  --chart-rgb-color5: ${hexToRGB(palette.chartColor5)};
  --chart-color5-tint: ${palette.chartColor5Tint};

  --notification-success-color: ${palette.notificationSuccess};
  --notification-primary-color: ${palette.notificationPrimary};
  --notification-warning-color: ${palette.notificationWarning};
  --notification-error-color: ${palette.notificationError};

  --icon-color: ${palette.icon};
  --icon-hover-color: ${palette.iconHover};
  --box-shadow: ${palette.boxShadow};
  --box-shadow-hover: ${palette.boxShadowHover};
  --box-shadow-nft-color: ${palette.boxShadowNft};
  --box-shadow-nft-secondary-color: ${palette.boxShadowNftSecondary};

  --heading-color: ${palette.heading};
  --item-hover-bg: ${palette.itemHoverBg};
  --background-base-color: ${palette.backgroundColorBase};
  --border-base-color: ${palette.borderBase};
  --disabled-color: ${palette.disable};
  --disabled-bg-color: ${palette.disabledBg};
  --layout-body-bg-color: ${palette.layoutBodyBg};
  --layout-header-bg-color: ${palette.layoutHeaderBg};
  --layout-sider-bg-color: ${palette.layoutSiderBg};
  --input-placeholder-color: ${palette.inputPlaceholder};
  --avatar-bg: ${palette.avatarBg};
  --alert-text-color: ${palette.alertTextColor};
  --breadcrumb-color: ${palette.breadcrumb};
`;

export const lightThemeVariables = css`
  ${getThemeVariables('light')}
`;

export const darkThemeVariables = css`
  ${getThemeVariables('dark')}
  --ant-success-color-deprecated-bg: ${antThemeObject['dark']
    .successBg} !important;
  --ant-success-color-deprecated-border: ${antThemeObject['dark']
    .successBorder} !important;
`;

export const commonThemeVariables = css`
  --white: ${BASE_COLORS.white};
  --black: ${BASE_COLORS.black};
  --green: ${BASE_COLORS.green};
  --orange: ${BASE_COLORS.orange};
  --gray: ${BASE_COLORS.gray};
  --lightgrey: ${BASE_COLORS.lightgrey};
  --violet: ${BASE_COLORS.violet};
  --lightgreen: ${BASE_COLORS.lightgreen};
  --pink: ${BASE_COLORS.pink};
  --blue: ${BASE_COLORS.blue};
  --skyblue: ${BASE_COLORS.skyblue};
  --red: ${BASE_COLORS.red};
`;

export const antOverrideCssVariables = css`
  --ant-primary-1: var(--primary1-color) !important;
`;
